import { Component, DOCUMENT, Inject, OnDestroy, OnInit, inject } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { TReducerState } from '../../../app.config';
import { EScreenLockEntityType } from '../../../core/services/screen-lock.interface';
import { ScreenLockService } from '../../../core/services/screen-lock.service';
import { PageHeaderService } from '../../../layout/page-header/page-header.service';
import { CountdownManager } from '../../../shared/components/countdown/countdown-manager.class';
import { SlideshowService } from '../slideshow.service';
import { EIframeState } from './slideshow-content.interface';
import { SlideshowControllerComponent } from './slideshow-controller/slideshow-controller.component';

@Component({
  imports: [SlideshowControllerComponent],
  selector: 'app-slideshow-content',
  styleUrls: ['./slideshow-content.component.scss'],
  templateUrl: './slideshow-content.component.html',
})
export class SlideshowContentComponent implements OnInit, OnDestroy {
  public dashboardUrls: SafeResourceUrl[] = [];
  public currentIframeUrl: SafeResourceUrl | null = null;
  public nextIframeUrl: SafeResourceUrl | null = null;
  public activeIframe: EIframeState = EIframeState.current;

  public isDragging = false;

  private currentDataIndex = 0;
  protected readonly EIframeState = EIframeState;

  public slideshow = this.globalStore.selectSignal((state) => state.slideshowContent.slideshowData!);
  public readonly countdownManager!: CountdownManager;
  private readonly subscriptions: Subscription[] = [];
  private preloadTimeoutId: NodeJS.Timeout | undefined;
  private readonly screenLockService = inject(ScreenLockService);

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private readonly globalStore: Store<TReducerState>,
    private readonly pageHeaderService: PageHeaderService,
    private sanitizer: DomSanitizer,
    private readonly route: ActivatedRoute,
    public readonly slideshowService: SlideshowService,
  ) {
    const { countdownManager } = this.route.snapshot.data;

    if (countdownManager) {
      this.countdownManager = countdownManager;
    }
  }

  public ngOnInit(): void {
    this.setUrls();
    this.initDoubleBuffer();
    this.startSlideshow();
    this.listenManualChange();

    this.startScreenLock();
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub?.unsubscribe());
    this.screenLockService.stop();

    if (this.preloadTimeoutId) {
      clearTimeout(this.preloadTimeoutId);
    }
  }

  private startScreenLock(): void {
    const slideshow = this.slideshow();
    const autoLock = slideshow?.autoLock;

    if (!slideshow || !autoLock) {
      return;
    }

    this.screenLockService.start(
      { entityId: slideshow.id, entityName: slideshow.name, entityType: EScreenLockEntityType.slideshow },
      { idleTimeoutSeconds: autoLock.durationSeconds },
    );
  }

  public onDragStarted(): void {
    this.isDragging = true;
  }

  public onDragEnded(): void {
    this.isDragging = false;
  }

  public onManualChange(direction: 'next' | 'prev'): void {
    this.changePage(direction);
    this.resetCountdown();
  }

  private listenManualChange(): void {
    if (this.dashboardUrls.length <= 1) {
      return;
    }

    this.subscriptions.push(
      this.countdownManager.manualChangeSubject.subscribe((direction: 'next' | 'prev') => {
        this.onManualChange(direction);
      }),
    );
  }

  private resetCountdown(): void {
    if (this.dashboardUrls.length > 1) {
      const duration = this.slideshow().slideDurationSeconds ?? -1;
      this.countdownManager.setCountdownSubject.next(duration);
    }
  }

  private setUrls(): void {
    this.dashboardUrls = [];

    if (!this.slideshow()) {
      return;
    }

    this.pageHeaderService.setTitle(this.slideshow().name);
    PageHeaderService.setPageHeader({ title: this.slideshow().name });

    const duration = this.slideshow().slideDurationSeconds ?? -1;
    this.countdownManager.setCountdownSubject.next(duration);

    const authorizedItems = this.slideshowService.extractAuthorizedItems(this.slideshow().items || [], true);

    authorizedItems.forEach((item) => {
      if (item.type === 'dashboard' && item.id) {
        this.dashboardUrls.push(
          this.sanitizer.bypassSecurityTrustResourceUrl(`dashboards/${item.id}?hidePageHeader=true&hideToolbar=true`),
        );
      } else if (item.type === 'url' && item.url) {
        this.dashboardUrls.push(this.sanitizer.bypassSecurityTrustResourceUrl(item.url));
      }
    });
  }

  private initDoubleBuffer(): void {
    if (this.dashboardUrls.length === 0) {
      return;
    }

    [this.currentIframeUrl, this.nextIframeUrl = null] = this.dashboardUrls;
  }

  private startSlideshow(): void {
    if (this.dashboardUrls.length <= 1) {
      return;
    }

    this.subscriptions.push(
      this.countdownManager.timeoutSubject.subscribe(() => {
        if (!this.countdownManager.pauseSubject.value) {
          this.changePage('next');
        }
      }),
    );
  }

  private changePage(direction: 'next' | 'prev' = 'next'): void {
    try {
      if ('startViewTransition' in this.document) {
        this.document.startViewTransition(() => {
          this.swapIframes(direction);
        });
      } else {
        this.swapIframes(direction);
      }
    } catch {
      this.swapIframes(direction);
    }
  }

  private swapIframes(direction: 'next' | 'prev'): void {
    if (direction === 'next') {
      this.currentDataIndex = (this.currentDataIndex + 1) % this.dashboardUrls.length;
    } else {
      this.currentDataIndex = (this.currentDataIndex - 1 + this.dashboardUrls.length) % this.dashboardUrls.length;
    }

    const targetUrl = this.dashboardUrls[this.currentDataIndex];

    if (this.activeIframe === EIframeState.current) {
      this.nextIframeUrl = targetUrl;
    } else {
      this.currentIframeUrl = targetUrl;
    }

    this.activeIframe = this.activeIframe === EIframeState.current ? EIframeState.next : EIframeState.current;

    if (this.preloadTimeoutId) clearTimeout(this.preloadTimeoutId);

    this.preloadTimeoutId = setTimeout(() => {
      const nextIdx = (this.currentDataIndex + 1) % this.dashboardUrls.length;

      if (this.activeIframe === EIframeState.current) {
        this.nextIframeUrl = this.dashboardUrls[nextIdx];
      } else {
        this.currentIframeUrl = this.dashboardUrls[nextIdx];
      }
    }, 1000);
  }
}
